import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTenantBranding } from "../branding/TenantBrandingProvider";

const SITE_URL = "https://velsoftcrm.com";
const DEFAULT_TITLE = "VelSoft CRM — Sales CRM, Leads, Tasks and Telegram Automation";
const DEFAULT_DESCRIPTION =
  "VelSoft CRM helps businesses manage contacts, leads, deals, tasks and customer history, with Telegram automation and flexible workflows in one workspace.";
const DEFAULT_OG_IMAGE = `${SITE_URL}/seo/velsoft-crm-og.png`;

function setMeta(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function setCanonical(href) {
  let element = document.head.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
}

function setStructuredData(enabled) {
  const scriptId = "velsoft-structured-data";
  let script = document.getElementById(scriptId);

  if (!enabled) {
    if (script) script.remove();
    return;
  }

  if (!script) {
    script = document.createElement("script");
    script.id = scriptId;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "VelSoft CRM",
        url: `${SITE_URL}/`,
        logo: `${SITE_URL}/logo512.png`,
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: `${SITE_URL}/`,
        name: "VelSoft CRM",
        publisher: { "@id": `${SITE_URL}/#organization` },
        inLanguage: "en",
      },
      {
        "@type": "SoftwareApplication",
        "@id": `${SITE_URL}/#software`,
        name: "VelSoft CRM",
        url: `${SITE_URL}/`,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description: DEFAULT_DESCRIPTION,
        publisher: { "@id": `${SITE_URL}/#organization` },
      },
    ],
  });
}

export default function SeoManager() {
  const location = useLocation();
  const { branding, isLoaded } = useTenantBranding();

  useEffect(() => {
    if (!isLoaded) return;

    const isMainPublicHome = location.pathname === "/" && Boolean(branding?.is_main);
    const isPrivateOrUtilityRoute =
      location.pathname.startsWith("/menu") ||
      location.pathname === "/login" ||
      location.pathname === "/employee-register";

    if (isMainPublicHome) {
      document.title = DEFAULT_TITLE;
      setCanonical(`${SITE_URL}/`);
      setMeta('meta[name="description"]', {
        name: "description",
        content: DEFAULT_DESCRIPTION,
      });
      setMeta('meta[name="robots"]', {
        name: "robots",
        content:
          "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      });
      setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
      setMeta('meta[property="og:site_name"]', {
        property: "og:site_name",
        content: "VelSoft CRM",
      });
      setMeta('meta[property="og:title"]', {
        property: "og:title",
        content: DEFAULT_TITLE,
      });
      setMeta('meta[property="og:description"]', {
        property: "og:description",
        content: DEFAULT_DESCRIPTION,
      });
      setMeta('meta[property="og:url"]', {
        property: "og:url",
        content: `${SITE_URL}/`,
      });
      setMeta('meta[property="og:image"]', {
        property: "og:image",
        content: DEFAULT_OG_IMAGE,
      });
      setMeta('meta[name="twitter:card"]', {
        name: "twitter:card",
        content: "summary_large_image",
      });
      setMeta('meta[name="twitter:title"]', {
        name: "twitter:title",
        content: DEFAULT_TITLE,
      });
      setMeta('meta[name="twitter:description"]', {
        name: "twitter:description",
        content: DEFAULT_DESCRIPTION,
      });
      setMeta('meta[name="twitter:image"]', {
        name: "twitter:image",
        content: DEFAULT_OG_IMAGE,
      });
      setStructuredData(true);
      return;
    }

    // The authenticated CRM, login, employee registration and tenant-specific
    // login pages should not appear in public search results.
    document.title = branding?.company_name
      ? `${branding.company_name} — CRM`
      : "VelSoft CRM";
    setCanonical(`${window.location.origin}${location.pathname}`);
    setMeta('meta[name="robots"]', {
      name: "robots",
      content: "noindex, nofollow, noarchive",
    });
    setStructuredData(false);

    if (!isPrivateOrUtilityRoute && location.pathname !== "/") {
      document.title = "404 — VelSoft CRM";
    }
  }, [branding?.company_name, branding?.is_main, isLoaded, location.pathname]);

  return null;
}
